import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { Building2, Cloud, Search, Download, MapPin } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import LocalAvulsoCampos, { avulsoVazio, avulsoPreenchido, cadastrarLocalAvulso } from '../components/LocalAvulsoCampos';

const BATCH_TIMEOUT_MS = 180000;
const BATCH_RETRY_ATTEMPTS = 3;
const SYNC_NUM_BATCHES = 8;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatarDataSync = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [syncingM8, setSyncingM8] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0, label: '' });
  const [syncResult, setSyncResult] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [showImportM8, setShowImportM8] = useState(false);
  const [showAvulso, setShowAvulso] = useState(false);
  const [avulsoForm, setAvulsoForm] = useState(avulsoVazio());
  const [savingAvulso, setSavingAvulso] = useState(false);
  const [m8Codigo, setM8Codigo] = useState('');
  const [importingM8, setImportingM8] = useState(false);
  const [importFeedback, setImportFeedback] = useState(null);
  const [editingSerieId, setEditingSerieId] = useState(null);
  const [serieEditValor, setSerieEditValor] = useState('');
  const [savingSerie, setSavingSerie] = useState(false);
  const [reparsingSeries, setReparsingSeries] = useState(false);

  useEffect(() => {
    const delay = search.trim() ? 350 : 0;
    const timer = setTimeout(() => {
      loadClientes();
    }, delay);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadCronStatus();
  }, []);

  const loadCronStatus = async () => {
    try {
      const { data } = await api.get('/clientes/m8/sync/status');
      setCronStatus(data);
    } catch (_) {
      setCronStatus(null);
    }
  };

  const loadClientes = async () => {
    try {
      const response = await api.get('/clientes', { params: { search, limit: 100 } });
      setClientes(response.data.clientes);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const postBatchWithRetry = async (batchIndex, sessionId) => {
    let lastError;
    for (let attempt = 1; attempt <= BATCH_RETRY_ATTEMPTS; attempt++) {
      try {
        return await api.post(
          `/clientes/m8/sync/batch/${batchIndex}`,
          null,
          {
            params: { session_id: sessionId },
            timeout: BATCH_TIMEOUT_MS,
          }
        );
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        const isNetwork =
          !error.response ||
          error.code === 'ECONNABORTED' ||
          error.message?.includes('Network Error');
        const retriable =
          isNetwork || !status || status >= 500 || status === 502 || status === 504;
        if (!retriable || attempt >= BATCH_RETRY_ATTEMPTS) {
          throw error;
        }
        toast.error(
          `Lote ${batchIndex + 1}: conexão interrompida (timeout/proxy) — ` +
          `tentando novamente (${attempt}/${BATCH_RETRY_ATTEMPTS})`
        );
        await sleep(2000 * attempt);
      }
    }
    throw lastError;
  };

  const sincronizarM8 = async () => {
    const confirmar = window.confirm(
      'Sincronizar cadastro com o M8?\n\n' +
      'Atualiza nome, endereço, CEP e vínculos OS dos participantes ' +
      'dos contratos. A operação é dividida em lotes e pode levar vários minutos.'
    );
    if (!confirmar) return;

    setSyncingM8(true);
    setSyncResult(null);
    setSyncProgress({ current: 0, total: 0, label: 'Preparando...' });

    try {
      const startBody = { num_batches: SYNC_NUM_BATCHES };
      const maxContratos = process.env.REACT_APP_M8_SYNC_MAX_CONTRATOS;
      if (maxContratos && /^\d+$/.test(maxContratos)) {
        startBody.max_contratos = parseInt(maxContratos, 10);
      }

      const startRes = await api.post('/clientes/m8/sync/batch/start', startBody, {
        timeout: BATCH_TIMEOUT_MS,
      });
      const start = startRes.data;

      if (!start.total_contratos) {
        const vazio = {
          ok: false,
          titulo: 'Sincronização sem contratos',
          mensagem: start.mensagem || 'Nenhum contrato ativo encontrado no M8',
        };
        setSyncResult(vazio);
        toast.error(vazio.mensagem);
        return;
      }

      const totalBatches = start.total_batches || 1;
      setSyncProgress({
        current: 0,
        total: totalBatches,
        label: `Preparando ${totalBatches} lote(s) · ${start.total_contratos} contratos`,
      });

      let criados = 0;
      let atualizados = 0;
      let inalterados = 0;
      let enderecos = 0;
      let participantes = 0;
      let contratosProcessados = 0;
      let errosContratos = 0;
      let errosEndereco = 0;
      let duracaoTotal = 0;
      let semVinculoAmostras = [];

      for (let i = 0; i < totalBatches; i++) {
        setSyncProgress({
          current: i,
          total: totalBatches,
          label: `Atualizando cadastro · lote ${i + 1} de ${totalBatches}`,
        });
        const batchRes = await postBatchWithRetry(i, start.session_id);
        const data = batchRes.data;
        criados += data.clientes_criados || 0;
        atualizados += data.clientes_atualizados || 0;
        inalterados += data.clientes_inalterados || 0;
        enderecos += data.enderecos_atualizados || 0;
        participantes += data.participantes_encontrados || 0;
        contratosProcessados += data.contratos_processados || 0;
        errosContratos += (data.erros_contratos || []).length;
        errosEndereco += (data.erros_endereco || []).length;
        duracaoTotal += data.duracao_segundos || 0;
        setSyncProgress({
          current: i + 1,
          total: totalBatches,
          label: `Lote ${i + 1} de ${totalBatches} concluído`,
        });
      }

      let semVinculo = null;
      try {
        const incompletosRes = await api.get('/clientes/m8/sem-vinculo-os', {
          params: { limit: 10 },
          timeout: 30000,
        });
        semVinculo = incompletosRes.data;
        semVinculoAmostras = (semVinculo.clientes || []).slice(0, 5).map((c) => c.codigo || c.m8_cliente_id);
      } catch (_) {
        /* endpoint opcional — não bloqueia o resumo */
      }

      const limite = start.limite_contratos
        ? `Limite: ${contratosProcessados}/${start.contratos_ativos_total} contratos.`
        : `${contratosProcessados} contratos processados.`;

      const resultado = {
        ok: errosContratos === 0,
        titulo: 'Sincronização concluída',
        criados,
        atualizados,
        inalterados,
        enderecos,
        participantes,
        contratosProcessados,
        totalBatches,
        duracaoSegundos: Math.round(duracaoTotal),
        errosContratos,
        errosEndereco,
        limite,
        semVinculoTotal: semVinculo?.total ?? null,
        semVinculoAmostras,
      };
      setSyncResult(resultado);
      setSyncProgress({
        current: totalBatches,
        total: totalBatches,
        label: 'Concluído',
      });

      const resumoToast =
        `Sync OK em ${resultado.duracaoSegundos}s: ` +
        `${criados} novos, ${atualizados} com atualização cadastral, ` +
        `${inalterados} conferidos sem mudança.` +
        (semVinculo?.total
          ? ` ${semVinculo.total} ainda sem vínculo OS.`
          : '');
      toast.success(resumoToast, { duration: 10000 });
      loadClientes();
      loadCronStatus();
    } catch (error) {
      const isCorsOrNetwork =
        !error.response ||
        error.message?.includes('Network Error') ||
        error.code === 'ERR_NETWORK';
      const msg = isCorsOrNetwork
        ? 'Conexão cortada no meio do lote (timeout do proxy). Tente de novo — lotes já gravados não se perdem.'
        : (error.response?.data?.detail || 'Erro ao sincronizar com M8');
      setSyncResult({ ok: false, titulo: 'Sincronização falhou', mensagem: msg });
      toast.error(msg);
    } finally {
      setSyncingM8(false);
    }
  };

  const importarClienteM8 = async () => {
    const cod = m8Codigo.trim();
    if (!cod) {
      toast.error('Informe o código M8');
      return;
    }
    setImportingM8(true);
    setImportFeedback(null);
    try {
      const response = await api.post('/clientes/m8/import', { m8_codigo: cod }, { timeout: 180000 });
      const data = response.data;
      if (data.warning || data.vinculo_os_completo === false) {
        setImportFeedback({
          ok: false,
          titulo: `Importado: ${data.nome}`,
          mensagem: data.warning || 'Alguns dados M8 ficaram incompletos — confira na lista.',
        });
      } else {
        setImportFeedback({ ok: true, titulo: `Importado: ${data.nome}`, mensagem: null });
        toast.success(`Cliente importado: ${data.nome}`);
      }
      setM8Codigo('');
      loadClientes();
    } catch (error) {
      const isNetwork =
        !error.response ||
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error');
      if (isNetwork) {
        // Proxy corta a resposta, mas o backend muitas vezes já gravou.
        try {
          await loadClientes();
          setImportFeedback({
            ok: true,
            titulo: `Cliente ${cod} pode ter sido importado`,
            mensagem: 'A conexão demorou — confira na lista abaixo.',
          });
          setM8Codigo('');
          return;
        } catch (_) {
          /* segue */
        }
      }
      setImportFeedback({
        ok: false,
        titulo: 'Falha na importação',
        mensagem: error.response?.data?.detail || 'Erro ao importar do M8',
      });
      toast.error(error.response?.data?.detail || 'Erro ao importar do M8');
    } finally {
      setImportingM8(false);
    }
  };

  const cadastrarAvulso = async () => {
    if (!avulsoPreenchido(avulsoForm)) {
      toast.error('Informe o nome e o endereço do local');
      return;
    }
    setSavingAvulso(true);
    try {
      const criado = await cadastrarLocalAvulso(avulsoForm);
      toast.success(`Local cadastrado: ${criado.nome}`);
      setAvulsoForm(avulsoVazio());
      setShowAvulso(false);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar local');
    } finally {
      setSavingAvulso(false);
    }
  };

  const iniciarEdicaoSerie = (cliente) => {
    setEditingSerieId(cliente.id);
    setSerieEditValor(cliente.numero_serie || '');
  };

  const salvarSerieCliente = async (clienteId) => {
    setSavingSerie(true);
    try {
      await api.patch(`/clientes/${clienteId}/numero-serie`, { numero_serie: serieEditValor.trim() });
      toast.success('Nº de série atualizado');
      setEditingSerieId(null);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar série');
    } finally {
      setSavingSerie(false);
    }
  };

  const reparseSeries = async () => {
    if (!window.confirm('Reextrair nº de série dos nomes?\n\nClientes com série manual não serão alterados.')) return;
    setReparsingSeries(true);
    try {
      const { data } = await api.post('/clientes/reparse-series');
      toast.success(data.mensagem || `${data.atualizados} atualizado(s)`);
      loadClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reextrair séries');
    } finally {
      setReparsingSeries(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clientes-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Clientes</h1>
            <p className="text-slate-600">{total} clientes cadastrados</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => {
              setShowAvulso(!showAvulso);
              if (!showAvulso) setShowImportM8(false);
            }}
            className="bg-white border border-amber-200 text-amber-800 px-5 py-3 rounded-xl font-medium hover:bg-amber-50 transition-all flex items-center gap-2"
            data-testid="cadastrar-avulso-btn"
          >
            <MapPin className="w-5 h-5" />
            Cadastrar local avulso
          </button>
          <button
            type="button"
            onClick={() => setShowImportM8(!showImportM8)}
            className="bg-white border border-violet-200 text-violet-700 px-5 py-3 rounded-xl font-medium hover:bg-violet-50 transition-all flex items-center gap-2"
            data-testid="import-cliente-m8-btn"
          >
            <Download className="w-5 h-5" />
            Importar do M8
          </button>
          <button
            type="button"
            onClick={sincronizarM8}
            disabled={syncingM8}
            className={`bg-gradient-to-r from-violet-500 to-purple-600 text-white px-6 py-3 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2 ${syncingM8 ? 'opacity-50 cursor-not-allowed' : ''}`}
            data-testid="sync-clientes-m8-btn"
          >
            {syncingM8 ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Cloud className="w-5 h-5" />
            )}
            {syncingM8 ? 'Sincronizando M8...' : 'Sincronizar com M8'}
          </button>
          <button
            type="button"
            onClick={reparseSeries}
            disabled={reparsingSeries}
            className="bg-white border border-slate-200 text-slate-700 px-5 py-3 rounded-xl font-medium hover:bg-slate-50 transition-all disabled:opacity-50"
            title="Extrai SERIE 415, SN 123, etc. dos nomes já cadastrados"
          >
            {reparsingSeries ? 'Extraindo...' : 'Extrair séries dos nomes'}
          </button>
        </div>
      </div>

      {cronStatus && (
        <div
          className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm text-slate-700 space-y-1"
          data-testid="sync-clientes-cron-status"
        >
          <p>
            Sync automática:{' '}
            {cronStatus.cron_ativo ? (
              <strong className="text-emerald-700">todo dia às {cronStatus.cron_horario}</strong>
            ) : (
              <strong className="text-amber-700">desligada</strong>
            )}
            <span className="text-slate-400"> ({cronStatus.cron_timezone || 'America/Sao_Paulo'})</span>
          </p>
          <p>
            Última atualização:{' '}
            {cronStatus.ultima_sync ? (
              <>
                <strong>{formatarDataSync(cronStatus.ultima_sync)}</strong>
                {cronStatus.ultima_sync_status && (
                  <span
                    className={
                      cronStatus.ultima_sync_status === 'sucesso'
                        ? ' text-emerald-700'
                        : cronStatus.ultima_sync_status === 'parcial'
                          ? ' text-amber-700'
                          : ' text-red-700'
                    }
                  >
                    {' '}· {cronStatus.ultima_sync_status}
                  </span>
                )}
                {cronStatus.ultima_sync_resumo?.origem === 'manual' && (
                  <span className="text-slate-400"> · manual</span>
                )}
              </>
            ) : (
              <span className="text-slate-400">ainda não rodou</span>
            )}
          </p>
          {cronStatus.ultima_sync_resumo?.atualizados != null && (
            <p className="text-slate-500">
              {cronStatus.ultima_sync_resumo.criados || 0} novos ·{' '}
              {cronStatus.ultima_sync_resumo.atualizados || 0} atualizados ·{' '}
              {cronStatus.ultima_sync_resumo.inalterados || 0} sem mudança
            </p>
          )}
        </div>
      )}

      {(syncingM8 || syncResult) && (
        <div
          className={`rounded-2xl p-4 space-y-3 border ${
            syncResult?.ok === false
              ? 'bg-red-50 border-red-200'
              : syncResult
                ? 'bg-emerald-50 border-emerald-200'
                : 'bg-violet-50 border-violet-200'
          }`}
          data-testid="sync-m8-progress"
        >
          {syncingM8 && syncProgress.total > 0 && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-violet-900">
                <span className="font-medium">{syncProgress.label || 'Sincronizando...'}</span>
                <span>
                  {Math.round((syncProgress.current / Math.max(syncProgress.total, 1)) * 100)}%
                </span>
              </div>
              <Progress value={(syncProgress.current / Math.max(syncProgress.total, 1)) * 100} />
              <p className="text-xs text-violet-700">
                Conferindo atualizações cadastrais no M8 (nome, CEP, vínculos OS). Não feche a página.
              </p>
            </div>
          )}

          {syncResult && !syncingM8 && (
            <div className="space-y-2 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`font-semibold ${syncResult.ok === false ? 'text-red-900' : 'text-emerald-900'}`}>
                    {syncResult.titulo}
                    {syncResult.ok !== false ? ' — cadastro conferido' : ''}
                  </p>
                  {syncResult.mensagem && (
                    <p className="text-red-800 mt-1">{syncResult.mensagem}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-xs underline text-slate-600 shrink-0"
                  onClick={() => setSyncResult(null)}
                >
                  Fechar
                </button>
              </div>
              {syncResult.ok !== false && syncResult.duracaoSegundos != null && (
                <ul className="grid sm:grid-cols-2 gap-1 text-emerald-950">
                  <li><strong>{syncResult.criados}</strong> novos clientes</li>
                  <li><strong>{syncResult.atualizados}</strong> com atualização cadastral</li>
                  <li><strong>{syncResult.inalterados}</strong> conferidos sem mudança</li>
                  <li><strong>{syncResult.enderecos}</strong> endereços consultados no M8</li>
                  <li><strong>{syncResult.participantes}</strong> participantes nos lotes</li>
                  <li><strong>{syncResult.duracaoSegundos}s</strong> · {syncResult.totalBatches} lote(s)</li>
                </ul>
              )}
              {syncResult.limite && (
                <p className="text-xs text-emerald-800">{syncResult.limite}</p>
              )}
              {(syncResult.errosContratos > 0 || syncResult.errosEndereco > 0) && (
                <p className="text-amber-800 text-xs">
                  Atenção: {syncResult.errosContratos || 0} erro(s) de contrato,
                  {' '}{syncResult.errosEndereco || 0} erro(s) de endereço — parte dos dados pode estar incompleta.
                </p>
              )}
              {syncResult.semVinculoTotal > 0 && (
                <p className="text-amber-900 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <strong>{syncResult.semVinculoTotal}</strong> cliente(s) no sistema ainda sem vínculo OS
                  (M8 Cliente OS / Participante / Contrato)
                  {syncResult.semVinculoAmostras?.length
                    ? ` — ex.: ${syncResult.semVinculoAmostras.join(', ')}`
                    : ''}
                  . Esses códigos existem no M8 mas não aparecem como participante de contrato ativo
                  (ex.: Unimed Itatiba 27703). Confira no ERP ou reimporte após vincular no contrato.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {showAvulso && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-amber-950">Local ainda sem cadastro M8</h2>
            <p className="text-xs text-amber-800 mt-0.5">
              Use para visita técnica em quem ainda não é cliente. Depois dá para incluir na rota.
            </p>
          </div>
          <LocalAvulsoCampos value={avulsoForm} onChange={setAvulsoForm} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={cadastrarAvulso}
              disabled={savingAvulso || !avulsoPreenchido(avulsoForm)}
              className="bg-amber-600 text-white px-5 py-2.5 rounded-xl font-medium disabled:opacity-50"
            >
              {savingAvulso ? 'Salvando…' : 'Salvar local'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAvulso(false); setAvulsoForm(avulsoVazio()); }}
              className="px-4 py-2.5 text-amber-800 hover:bg-amber-100 rounded-xl"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {showImportM8 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-violet-900 mb-1">Código M8 (PessoaId)</label>
              <input
                type="text"
                inputMode="numeric"
                value={m8Codigo}
                onChange={(e) => setM8Codigo(e.target.value)}
                placeholder="Ex.: 29741"
                className="w-full px-4 py-3 rounded-xl border border-violet-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-200 outline-none"
                data-testid="import-m8-codigo-input"
                onKeyDown={(e) => e.key === 'Enter' && importarClienteM8()}
              />
            </div>
            <button
              type="button"
              onClick={importarClienteM8}
              disabled={importingM8}
              className="bg-violet-600 text-white px-6 py-3 rounded-xl font-medium disabled:opacity-50 flex items-center gap-2"
            >
              {importingM8 ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Download className="w-5 h-5" />
              )}
              {importingM8 ? 'Importando…' : 'Importar'}
            </button>
            <button
              type="button"
              onClick={() => { setShowImportM8(false); setM8Codigo(''); setImportFeedback(null); }}
              className="px-4 py-3 text-violet-700 hover:bg-violet-100 rounded-xl"
            >
              Cancelar
            </button>
          </div>
          {importFeedback && (
            <div
              className={`rounded-xl px-3 py-2 text-sm ${
                importFeedback.ok
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                  : 'bg-amber-50 border border-amber-200 text-amber-950'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">{importFeedback.titulo}</p>
                  {importFeedback.mensagem && (
                    <p className="mt-1 text-xs leading-relaxed break-words">{importFeedback.mensagem}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="text-xs underline shrink-0"
                  onClick={() => setImportFeedback(null)}
                >
                  Fechar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome, código, CEP ou série..." className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none" data-testid="search-clientes-input" />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Código</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Nome</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Nº Série</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">M8 Pessoa</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">M8 Cliente OS</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">M8 Participante</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">M8 Contrato</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Equip. M8</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">CEP</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Cidade</th>
                <th className="text-left py-4 px-6 text-sm font-medium text-slate-600">Distância</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((cliente) => (
                <tr key={cliente.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="py-4 px-6 text-sm font-medium text-slate-900">
                    {cliente.codigo}
                    {cliente.avulso && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-amber-100 text-amber-800">
                        Avulso
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-900">{cliente.nome}</td>
                  <td className="py-4 px-6 text-sm">
                    {editingSerieId === cliente.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={serieEditValor}
                          onChange={(e) => setSerieEditValor(e.target.value)}
                          className="w-24 px-2 py-1 border border-slate-300 rounded text-sm"
                          placeholder="Série"
                          autoFocus
                        />
                        <button
                          type="button"
                          disabled={savingSerie}
                          onClick={() => salvarSerieCliente(cliente.id)}
                          className="text-emerald-600 font-medium text-xs"
                        >
                          OK
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingSerieId(null)}
                          className="text-slate-500 text-xs"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => iniciarEdicaoSerie(cliente)}
                        className="text-left hover:text-emerald-700"
                        title={cliente.serie_fonte ? `Fonte: ${cliente.serie_fonte}` : 'Clique para editar'}
                      >
                        {cliente.numero_serie || (
                          <span className="text-slate-400 italic">—</span>
                        )}
                      </button>
                    )}
                  </td>
                  <td className="py-4 px-6 text-sm text-violet-600 font-medium">
                    {cliente.m8_cliente_id || '-'}
                  </td>
                  <td className="py-4 px-6 text-sm text-violet-700 font-medium">
                    {cliente.m8_os_cliente_id || '-'}
                  </td>
                  <td className="py-4 px-6 text-sm text-violet-800 font-medium">
                    {cliente.m8_participante_id || '-'}
                  </td>
                  <td className="py-4 px-6 text-sm text-violet-900 font-medium">
                    {cliente.m8_contrato_id || '-'}
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-700">
                    {cliente.m8_equipamento_nome || '-'}
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-600">{cliente.cep || '-'}</td>
                  <td className="py-4 px-6 text-sm text-slate-600">{cliente.cidade || '-'}</td>
                  <td className="py-4 px-6 text-sm text-emerald-600 font-medium">
                    {cliente.distancia_viafiltros_km ? `${cliente.distancia_viafiltros_km.toFixed(1)} km` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clientes.length === 0 && (
          <div className="text-center py-12 text-slate-500">Nenhum cliente encontrado</div>
        )}
      </div>
    </div>
  );
};

export default Clientes;
